import { useEffect, useState } from "react";
import { X } from "lucide-react";
import DailyCheckin from "./DailyCheckin";
import "./moods.css";

// A gentle once-a-day check-in popup for the dashboard. It appears only
// when nothing has been logged today and the person has not dismissed it
// today, so it never nags. The compact composer lets them log a line and a
// mood right here; saving or "not now" both quiet it for the rest of the
// day.

function localToday() {
  const d = new Date();
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 10);
}
function daysSince(day) {
  if (!day) return null;
  try {
    const d = new Date(day + "T00:00:00");
    const t = new Date(); t.setHours(0, 0, 0, 0);
    return Math.round((t - d) / 86400000);
  } catch (_e) { return null; }
}

export default function CheckinPopup({ lastNoteDay, ready = true, onSaved, delayMs = 700 }) {
  const todayKey = localToday();
  const dismissedKey = "klareet.checkin.popup.dismissed";

  const gap = daysSince(lastNoteDay);
  const alreadyToday = gap !== null && gap <= 0;
  const dismissed = typeof window !== "undefined" && localStorage.getItem(dismissedKey) === todayKey;

  const [open, setOpen] = useState(false);

  const quiet = () => {
    try { localStorage.setItem(dismissedKey, todayKey); } catch (_e) { /* ignore */ }
    setOpen(false);
  };
  const handleSaved = (data) => {
    try { localStorage.setItem(dismissedKey, todayKey); } catch (_e) { /* ignore */ }
    if (onSaved) onSaved(data);
    setTimeout(() => setOpen(false), 900);
  };

  useEffect(() => {
    if (!ready || alreadyToday || dismissed) return undefined;
    const t = setTimeout(() => setOpen(true), delayMs);
    return () => clearTimeout(t);
  }, [ready, alreadyToday, dismissed, delayMs]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") quiet(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="mf-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Daily check-in"
      data-testid="checkin-popup"
      onMouseDown={(e) => { if (e.target === e.currentTarget) quiet(); }}
    >
      <div className="mf-popup-card" data-testid="checkin-popup-card">
        <div className="mf-popup-head">
          <div>
            <span className="mf-eyebrow">Daily check-in</span>
            <h3 className="mf-popup-title">a line for today?</h3>
          </div>
          <button
            type="button"
            className="mf-popup-x"
            onClick={quiet}
            aria-label="not now"
            title="not now"
            data-testid="checkin-popup-dismiss"
          >
            <X size={16} />
          </button>
        </div>
        <p className="mf-popup-sub">one honest sentence keeps your streak alive, and it stays private to you.</p>
        <DailyCheckin
          compact
          bare
          accent={false}
          onSaved={handleSaved}
          textareaTestid="checkin-popup-note"
          saveTestid="checkin-popup-save"
          moodTestid="checkin-popup-mood"
          rootTestid="checkin-popup-composer"
        />
      </div>
    </div>
  );
}
